import React from 'react'
import Person from '@mui/icons-material/Person'
import { Avatar, Box, Button, Chip, ListItemAvatar } from '@mui/material'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'
import { getOverallStatusColor, getSectionStatusColor, getStatusLabel } from '../utils/kycApprovalUtils'

/**
 * Reusable list component for displaying therapists or approval requests
 */
export default function KycApprovalsList({ items = [], mode, onItemClick, tabLabel }) {
  if (items.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, color: 'text.secondary' }}>
        No {tabLabel?.toLowerCase() || 'items'} found
      </Box>
    )
  }

  // Sort by name
  const sortedItems = [...items].sort((a, b) => {
    const nameA = a.therapistName || a.name || ''
    const nameB = b.therapistName || b.name || ''
    return nameA.localeCompare(nameB)
  })

  return (
    <div className="d-flex flex-column align-items-center w-100">
      {sortedItems.map((item) => {
        const isRequest = mode === 'request-list'
        const name = isRequest ? item.therapistName : item.name
        const phone = isRequest ? item.therapistPhone : item.phone
        const profilePicId = isRequest ? null : item.profilePicId // Requests don't have profilePicId directly

        return (
          <div
            className="d-flex flex-column w-100 align-items-center"
            style={{ position: 'relative' }}
            key={item.id}
          >
            <Box
              sx={{
                width: '100%',
                bgcolor: 'background.paper',
                display: 'flex',
                alignItems: 'center',
                p: 1,
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <ListItemAvatar>
                <Avatar src={profilePicId ? getImageLink(profilePicId) : null}>
                  <Person />
                </Avatar>
              </ListItemAvatar>

              <div className="d-flex flex-column gap-2" style={{ flex: 1 }}>
                {/* Name and status badges */}
                <div className="d-flex align-items-center gap-2" style={{ flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 500 }}>{name}</span>

                  {phone && (
                    <span style={{ fontSize: '0.85rem', color: '#666' }}>{phone}</span>
                  )}

                  {/* Show status chips for requests */}
                  {isRequest && (
                    <>
                      <Chip
                        label={getStatusLabel(item.overallStatus)}
                        color={getOverallStatusColor(item.overallStatus)}
                        size="small"
                        sx={{ height: '20px', fontSize: '0.7rem' }}
                      />
                      {item.applicationRef && (
                        <Chip
                          label={item.applicationRef}
                          size="small"
                          variant="outlined"
                          sx={{ height: '20px', fontSize: '0.7rem' }}
                        />
                      )}
                    </>
                  )}
                </div>

                {/* Section statuses for requests */}
                {isRequest && (
                  <div className="d-flex align-items-center gap-1" style={{ flexWrap: 'wrap' }}>
                    <SectionStatusChip label="KYC" status={item.kycStatus} />
                    <SectionStatusChip label="Functions" status={item.functionsStatus} />
                    <SectionStatusChip label="Assignments" status={item.assignmentsStatus} />
                    <SectionStatusChip label="Permissions" status={item.permissionsStatus} />
                    <SectionStatusChip label="Payout" status={item.payoutStatus} />
                    <SectionStatusChip label="Ops" status={item.opsStatus} />
                  </div>
                )}

                {/* Action button */}
                <div className="d-flex align-items-center gap-1">
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                    onClick={() => onItemClick(item)}
                  >
                    {isRequest ? 'View / Update' : 'Create Approval Request'}
                  </Button>
                </div>
              </div>
            </Box>

            {/* Divider */}
            <div
              style={{
                width: '80%',
                borderBottom: '1px solid #0000001f',
                position: 'absolute',
                bottom: '0px',
              }}
            />
          </div>
        )
      })}
    </div>
  )
}

/**
 * Small chip showing section status
 */
function SectionStatusChip({ label, status }) {
  return (
    <Chip
      label={`${label}: ${getStatusLabel(status)}`}
      color={getSectionStatusColor(status)}
      size="small"
      variant="outlined"
      sx={{
        height: '18px',
        fontSize: '0.65rem',
        '& .MuiChip-label': { px: 0.5, py: 0 },
      }}
    />
  )
}
